import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Shield, Users } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useInvitationDetail,
  useAcceptInvitation,
  type InvitationDetail,
  type MemberPermissionTree,
} from '@/api/teams';
import { actionLabel } from './shared';

// ---- Permission row model -----------------------------------------------

interface PermissionRow {
  id: string;
  service_id: string;
  service_name: string;
  /** null = whole-service grant */
  resource_type: string | null;
  /** null = type-level grant (all resources of that type) */
  resource_id: string | null;
  actions: string[];
}

function scopeLabel(row: PermissionRow): string {
  if (!row.resource_type) return 'Whole service';
  if (!row.resource_id) return `All ${row.resource_type}`;
  return row.resource_id;
}

function transformPermissionsToRows(
  tree: MemberPermissionTree | undefined,
  serviceNames?: Record<string, string>,
): PermissionRow[] {
  if (!tree?.permissions) return [];

  const rows: PermissionRow[] = [];
  let idx = 0;

  for (const [serviceId, svcPerm] of Object.entries(tree.permissions)) {
    const serviceName = serviceNames?.[serviceId] ?? serviceId;

    if (svcPerm.service_level && svcPerm.service_level.length > 0) {
      rows.push({
        id: String(idx++),
        service_id: serviceId,
        service_name: serviceName,
        resource_type: null,
        resource_id: null,
        actions: svcPerm.service_level,
      });
    }

    if (svcPerm.resources) {
      for (const [resourceType, resPerm] of Object.entries(svcPerm.resources)) {
        if (resPerm.type_level && resPerm.type_level.length > 0) {
          rows.push({
            id: String(idx++),
            service_id: serviceId,
            service_name: serviceName,
            resource_type: resourceType,
            resource_id: null,
            actions: resPerm.type_level,
          });
        }
        if (resPerm.specific) {
          for (const [resourceId, actions] of Object.entries(resPerm.specific)) {
            rows.push({
              id: String(idx++),
              service_id: serviceId,
              service_name: serviceName,
              resource_type: resourceType,
              resource_id: resourceId,
              actions,
            });
          }
        }
      }
    }
  }

  return rows;
}

// ---- Column definitions --------------------------------------------------

const COLUMNS: ColumnDef<PermissionRow, unknown>[] = [
  {
    id: 'service',
    header: 'Service',
    accessorKey: 'service_name',
    cell: ({ row }) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontWeight: 500 }}>
        <Shield size={14} color="var(--vn-fg-muted)" />
        {row.original.service_name}
      </span>
    ),
  },
  {
    id: 'scope',
    header: 'Scope',
    enableSorting: false,
    cell: ({ row }) => {
      const label = scopeLabel(row.original);
      const tone = !row.original.resource_type
        ? 'info'
        : !row.original.resource_id
          ? 'accent'
          : 'neutral';
      const mono = !!row.original.resource_id;
      return (
        <Badge tone={tone} appearance="outline" size="sm">
          <span style={mono ? { fontFamily: 'var(--vn-font-mono)' } : undefined}>{label}</span>
        </Badge>
      );
    },
  },
  {
    id: 'permissions',
    header: 'Permissions',
    enableSorting: false,
    cell: ({ row }) => (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
        {row.original.actions.includes('all') ? (
          <Badge tone="primary" appearance="subtle" size="sm">Full Access</Badge>
        ) : (
          row.original.actions.map((a) => (
            <Badge key={a} tone="neutral" appearance="subtle" size="sm">
              {actionLabel(a)}
            </Badge>
          ))
        )}
      </div>
    ),
  },
];

// ---- Detail rows helper --------------------------------------------------

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
      <dt style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 'var(--vn-font-size-md)' }}>{value || '—'}</dd>
    </div>
  );
}

// ---- Page ---------------------------------------------------------------

function ReviewContent({ detail, memberId }: { detail: InvitationDetail; memberId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();

  const accept = useAcceptInvitation({
    onSuccess: () => {
      show({ tone: 'success', title: `Joined "${detail.team_name}"` });
      navigate(detail.team_id ? `/teams/${detail.team_id}` : '/');
    },
    onError: () => show({ tone: 'danger', title: 'Could not accept invitation' }),
  });

  const rows = useMemo(
    () => transformPermissionsToRows(detail.permissions, detail.service_names),
    [detail.permissions, detail.service_names],
  );

  const handleAccept = () => accept.mutate({ member_id: memberId });

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Team information */}
      <Card>
        <CardHeader>
          <CardTitle>Team information</CardTitle>
          <CardDescription>You have been invited to join this team.</CardDescription>
        </CardHeader>
        <CardBody>
          <dl
            style={{
              display: 'grid',
              gap: 'var(--vn-space-4)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              margin: 0,
            }}
          >
            <DetailRow label="Team name" value={detail.team_name} />
            <DetailRow label="Invited by" value={detail.team_owner ?? '—'} />
            {detail.roles && detail.roles.length > 0 && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                <dt style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                  Your roles
                </dt>
                <dd style={{ margin: 0, display: 'flex', gap: 'var(--vn-space-1)', flexWrap: 'wrap' }}>
                  {detail.roles.map((r) => (
                    <Badge key={r} tone="primary" appearance="subtle" size="sm">
                      {r}
                    </Badge>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </CardBody>
      </Card>

      {/* Permissions */}
      <Card>
        <CardHeader>
          <CardTitle>Shared services &amp; permissions</CardTitle>
          <CardDescription>
            {rows.length > 0
              ? `${rows.length} permission rule${rows.length !== 1 ? 's' : ''} granted to you.`
              : 'No specific resources shared yet — you may get organisation defaults.'}
          </CardDescription>
        </CardHeader>
        <CardBody>
          {rows.length === 0 ? (
            <EmptyState
              icon={<Shield size={24} />}
              title="No explicit permissions yet"
              description="Contact the team owner to request access to specific services."
              size="sm"
            />
          ) : (
            <DataTable columns={COLUMNS} data={rows} getRowId={(r) => r.id} />
          )}
        </CardBody>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Accept invitation</CardTitle>
          <CardDescription>
            By accepting, you'll become a member of "{detail.team_name}" with the permissions listed
            above.
          </CardDescription>
        </CardHeader>
        <CardFooter style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
          <Button disabled={accept.isPending} onClick={handleAccept}>
            {accept.isPending ? 'Accepting…' : 'Accept invitation'}
          </Button>
          <Button variant="secondary" disabled={accept.isPending} onClick={() => navigate('/')}>
            Decline
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export default function InvitationReviewPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();

  const { data: detail, isLoading, isError } = useInvitationDetail(memberId);

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 36, width: 280, marginBottom: 'var(--vn-space-6)' }} />
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Skeleton style={{ height: 140 }} />
          <Skeleton style={{ height: 200 }} />
        </div>
      </Container>
    );
  }

  if (isError || !detail) {
    return (
      <Container size="xl">
        <PageHeader
          icon={<Users size={22} />}
          title="Invitation not found"
          description="This invitation may have already been accepted, expired, or doesn't exist."
        />
        <Alert tone="danger" title="Invitation unavailable" style={{ marginBottom: 'var(--vn-space-4)' }}>
          Please check your active invitations on the dashboard.
        </Alert>
        <Button variant="secondary" startIcon={<ArrowLeft size={15} />} onClick={() => navigate('/')}>
          Back to dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Users size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild>
              <Link to="/">Dashboard</Link>
            </BreadcrumbItem>
            <BreadcrumbItem current>Invitation</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={`Invitation to ${detail.team_name}`}
        description="Review the shared services and permissions before you join."
      />
      <ReviewContent detail={detail} memberId={memberId!} />
    </Container>
  );
}
